import { useState } from "react";
import { X, Save, Crown, Trash2, Plus } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel, StarDivider } from "@/components/common";
import { toast } from "sonner";

export function RosterEdit({ team, players, tournament, clans = [], onClose, onDone }) {
  const kdRequirement = tournament
    ? (
        tournament.kd_requirement ||
        (tournament.require_kd ? "verified" : "none")
      )
    : "none";
  const requireKd = ["indicative", "verified"].includes(
    kdRequirement
  );
  const maxp = (tournament && tournament.max_players) || 6;
  const minp = (tournament && tournament.min_players) || 1;
  const byId = Object.fromEntries(players.map((p) => [p.id, p]));

  const init = team.player_ids.map((pid) => {
    const p = byId[pid] || {};
    return { id: pid, discord_username: p.discord_username || "", activision_id: p.activision_id || "",
             discord_id: p.discord_id || "", kd: p.kd ?? "" };
  });
  const [rows, setRows] = useState(init.length ? init : [{ id: null, discord_username: "", activision_id: "", discord_id: "", kd: "" }]);
  const [captainIdx, setCaptainIdx] = useState(Math.max(0, team.player_ids.indexOf(team.captain_id)));
  const [name, setName] = useState(team.name);
  const [clanId, setClanId] = useState(team.clan_id || "");
  const [saving, setSaving] = useState(false);
  const selectedClan = clans.find((clan) => clan.id === clanId);

  const setR = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => { if (rows.length >= maxp) { toast.error(`Massimo ${maxp} giocatori`); return; } setRows([...rows, { id: null, discord_username: "", activision_id: "", discord_id: "", kd: "" }]); };
  const removeRow = (i) => {
    if (rows.length <= minp) { toast.error(`Minimo ${minp} giocatori`); return; }
    setRows((rs) => rs.filter((_, idx) => idx !== i));
    if (captainIdx >= rows.length - 1) setCaptainIdx(0);
  };

  const submit = async () => {
    for (const [i, r] of rows.entries()) {
      if (!r.activision_id.trim()) { toast.error(`Activision ID obbligatorio per il giocatore ${i + 1}`); return; }
      if (r.discord_id.trim() && !/^\d+$/.test(r.discord_id.trim())) { toast.error(`Discord ID non valido per il giocatore ${i + 1}: deve essere numerico`); return; }
      if (requireKd && (r.kd === "" || isNaN(+r.kd))) { toast.error(`K/D obbligatorio (numerico) per il giocatore ${i + 1}`); return; }
    }
    if (!name.trim()) { toast.error("Nome squadra obbligatorio"); return; }
    if (!clanId) { toast.error("Clan obbligatorio"); return; }

    const payload = {
      name: name.trim(), clan_id: clanId, captain_index: captainIdx,
      players: rows.map((r) => ({
        id: r.id || null, discord_id: r.discord_id.trim() || null,
        discord_username: r.discord_username.trim() || null, activision_id: r.activision_id.trim(),
        ...(requireKd || r.kd !== "" ? { kd: +r.kd } : {}),
      })),
    };
    setSaving(true);
    try {
      await api.put(`/teams/${team.id}/roster`, payload);
      toast.success("Iscrizione aggiornata · bot e classificazioni sincronizzati");
      onDone && onDone(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const input = (val, onChange, ph, testid, type = "text") => (
    <input type={type} value={val} onChange={(e) => onChange(e.target.value)} placeholder={ph} data-testid={testid}
      className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white text-sm focus:border-[var(--gold)] focus:outline-none" />
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="roster-edit-modal">
      <Panel gold className="p-6 w-full max-w-3xl my-8 boot-in">
        <div className="flex justify-between items-center mb-1">
          <h3 className="heading text-3xl gold-text">Modifica Iscrizione</h3>
          <button onClick={onClose} data-testid="roster-close" className="text-[var(--silver)] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="font-rajdhani text-xs text-[var(--silver)] uppercase tracking-widest mb-4">
          {tournament ? tournament.name : "Senza torneo"}{requireKd ? " · K/D richiesto" : ""}
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Nome Squadra</label>{input(name, setName, "Nome", "roster-team-name")}</div>
          <div>
            <label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Clan</label>
            <select value={clanId} onChange={(e) => setClanId(e.target.value)} data-testid="roster-team-clan"
              className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white text-sm focus:border-[var(--gold)] focus:outline-none">
              <option value="">— Seleziona clan —</option>
              {clans.map((clan) => (
                <option key={clan.id} value={clan.id}>{clan.name}{clan.tag ? ` [${clan.tag}]` : ""}</option>
              ))}
            </select>
            <div className="mt-1 font-rajdhani text-[10px] uppercase tracking-wider text-[var(--silver)]">
              Tag canonico: <span className="text-[var(--gold)]">{selectedClan?.tag || team.tag || "—"}</span>
            </div>
          </div>
        </div>

        <StarDivider label="Giocatori" className="mb-3" />
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className={`border clip-beveled-sm p-3 ${captainIdx === i ? "border-[var(--gold)]/60" : "border-[var(--steel)]/30"}`} data-testid={`roster-player-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <button onClick={() => setCaptainIdx(i)} data-testid={`roster-captain-${i}`} className={`font-rajdhani text-[10px] uppercase tracking-widest flex items-center gap-1 ${captainIdx === i ? "text-[var(--gold)]" : "text-[var(--silver)] hover:text-white"}`}>
                  <Crown className="w-3.5 h-3.5" /> {captainIdx === i ? "Caposquadra" : "Rendi capitano"}
                </button>
                <button onClick={() => removeRow(i)} data-testid={`roster-remove-${i}`} className="text-[var(--destructive)] hover:opacity-80"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className={`grid gap-2 ${requireKd ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
                {input(r.discord_username, (v) => setR(i, "discord_username", v), "Nickname Discord (opz.)", `roster-name-${i}`)}
                {input(r.activision_id, (v) => setR(i, "activision_id", v), "Activision ID *", `roster-act-${i}`)}
                {input(r.discord_id, (v) => setR(i, "discord_id", v), "Discord ID numerico (opz.)", `roster-did-${i}`)}
                {requireKd && input(r.kd, (v) => setR(i, "kd", v), "K/D", `roster-kd-${i}`, "number")}
              </div>
            </div>
          ))}
        </div>
        <button onClick={addRow} data-testid="roster-add" className="btn-outline-gold clip-beveled-sm mt-3 px-4 py-1.5 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Aggiungi Giocatore</button>

        <div className="flex gap-3 mt-5">
          <button onClick={submit} disabled={saving} data-testid="roster-submit" className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"><Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva Iscrizione"}</button>
          <button onClick={onClose} className="btn-outline-gold clip-beveled px-6 py-2.5">Annulla</button>
        </div>
      </Panel>
    </div>
  );
}
